import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import moment from "moment";
import ContentEditorDialog from "src/components/common/ContentEditorDialog";

import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError } from "src/utils/utils";

const PrivacyPolicyPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [privacyData, setPrivacyData] = useState({});
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editorValue, setEditorValue] = useState("");
  const [saving, setSaving] = useState(false);

  /* ================= FETCH PRIVACY ================= */
  const fetchData = () => {
    setLoading(true);
    axiosInstance
      .get(ApiEndPoints.PRIVACY_POLICY.get("privacy_policy"))
      .then((response) => {
        setPrivacyData(response?.data?.data?.legalContent || {});
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= DIALOG HANDLERS ================= */
  const handleOpen = () => {
    setEditorValue(privacyData?.content || "");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  /* ================= UPDATE PRIVACY ================= */
  const handleUpdatePrivacy = async () => {
    setSaving(true);
    try {
      await axiosInstance.patch(
        ApiEndPoints.PRIVACY_POLICY.updatePrivacy("privacy_policy"),
        {
          content: editorValue,
        }
      );
      fetchData();
      setOpen(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* ================= HEADER ================= */}
        <Box sx={{ p: { xs: 4, md: 6 }, display: "flex", justifyContent: "space-between", textAlign: "flex-start" }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "black",
                fontFamily: "Magoat, sans-serif",
              }}
            >
              Privacy Policy
            </Typography>

            <Typography
              sx={{
                mt: 2,
                color: "black",
              }}
            >
              How we collect, use, and protect your personal information
            </Typography>

            {privacyData?.lastUpdated && (
              <Chip
                label={moment(privacyData.lastUpdated).format("MMMM D, YYYY")}
                sx={{ mt: 3 }}
              />
            )}
          </Box>


          <Box>
            <Button variant="contained" onClick={handleOpen}>
              Edit Privacy
            </Button>
          </Box>
        </Box>

        {/* ================= CONTENT ================= */}
        <Box sx={{ p: { xs: 3, md: 6 } }}>
          <Box
            className="privacy-content"
            sx={{
              fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
              lineHeight: 1.8,
              color: theme.palette.text.primary,

              /* 🔥 REQUIRED FOR WRAPPING */
              width: "100%",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              whiteSpace: "normal",

              "& p, & span": {
                maxWidth: "100%",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              },

              "& p": {
                marginBottom: "16px",
              },

              "& h1, & h2, & h3, & h4, & h5, & h6": {
                fontWeight: 600,
                margin: "24px 0 12px",
                lineHeight: 1.4,
              },

              "& ul, & ol": {
                paddingLeft: { xs: "20px", md: "28px" },
                marginBottom: "16px",
              },

              "& li": {
                marginBottom: "8px",
              },

              "& img": {
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
                margin: "16px 0",
              },

              "& iframe, & video": {
                maxWidth: "100%",
                borderRadius: "8px",
              },

              "& blockquote": {
                borderLeft: "4px solid #ddd",
                paddingLeft: "16px",
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                margin: "16px 0",
              },

              "& code": {
                backgroundColor: "#f5f5f5",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "0.9em",
              },

              "& pre": {
                backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "8px",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: privacyData?.content || "",
            }}
          />

          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              By using our service, you acknowledge that you have read and understood
              this Privacy Policy.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <ContentEditorDialog
        open={open}
        onClose={handleClose}
        title="Edit Privacy Policy"
        editorValue={editorValue}
        onEditorChange={setEditorValue}
        onSave={handleUpdatePrivacy}
        saving={saving}
      />
    </Container>
  );
};

export default PrivacyPolicyPage;